import {useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Plus, Save, SlidersHorizontal, Trash2, Zap} from "lucide-react";
import {mapApiError} from "../../../shared/api/errors";
import {useTranslation} from "../../../shared/i18n";
import {useCapabilities} from "../../../shared/auth/CapabilitiesContext";
import {ConfirmationDialog} from "../../../shared/components/ConfirmationDialog";
import {MutationResultBanner} from "../../../shared/components/MutationResultBanner";
import {
    deleteReferralSettings,
    getReferralSettings,
    loadBranchStaff,
    loadReferralSettings,
    quickSetupReferralSettings,
    updateReferralSettings,
} from "../api";
import {referralSettingsToDraft, validateReferralDraft} from "../contracts";
import "../referralSettings.css";

const DEFAULT_SELECTOR = "default";

const MODE_VALUES = ["all_to_place", "all_to_staff", "top_1", "custom"];
const PRESET_MODE_VALUES = MODE_VALUES.filter((value) => value !== "custom");

const isNotFound = (error) => error?.response?.status === 404;
const isConflict = (error) => error?.response?.status === 409;

const referralApiError = (error) => {
    const serverMessage = error?.response?.data?.message;
    if (isConflict(error) && typeof serverMessage === "string" && serverMessage.trim()) {
        return {kind: "conflict", message: serverMessage.slice(0, 500), fieldErrors: {}};
    }
    return mapApiError(error);
};

const inviterMatches = (settings, inviterId) => (
    inviterId === null
        ? settings?.inviter_personal === null || settings?.inviter_personal === undefined
        : Number(settings?.inviter_personal) === inviterId
);

const settingsKey = (branchKey, inviterId) => [
    "admin-referral-settings",
    branchKey,
    inviterId === null ? DEFAULT_SELECTOR : String(inviterId),
];

const totalPercent = (shares) => shares.reduce((total, share) => {
    if (share.percent === "" || share.percent === null || share.percent === undefined) return total;
    const percent = Number(share.percent);
    return Number.isFinite(percent) ? total + percent : total;
}, 0);

const displayPercent = (value) => Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(2)));

const sharePayload = (share) => ({
    personal_id: share.is_place_share
        || share.personal_id === null
        || share.personal_id === undefined
        || share.personal_id === ""
        ? null
        : Number(share.personal_id),
    is_place_share: Boolean(share.is_place_share),
    percent: Number(share.percent),
});

export const ReferralSettingsWorkspace = ({branchId, branchName}) => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const canManage = capabilities.hasAction("referral.manage");
    const queryClient = useQueryClient();
    const branchKey = String(branchId || "");
    const listQueryKey = useMemo(
        () => ["admin-referral-settings-list", branchKey],
        [branchKey],
    );
    const nextRowKey = useRef(0);
    const hydratedSettingsSignature = useRef(null);
    const [selectedKey, setSelectedKey] = useState(DEFAULT_SELECTOR);
    const [draft, setDraft] = useState({distribution_type: "all_to_place", shares: []});
    const [validationMessage, setValidationMessage] = useState("");
    const [result, setResult] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const selectedInviterId = selectedKey === DEFAULT_SELECTOR ? null : Number(selectedKey);

    const staffQuery = useQuery({
        queryKey: ["admin-branch-staff", branchKey],
        queryFn: () => loadBranchStaff(branchId),
        enabled: canManage && Boolean(branchId),
    });
    const listQuery = useQuery({
        queryKey: listQueryKey,
        queryFn: () => loadReferralSettings(branchId),
        enabled: canManage && Boolean(branchId),
    });
    const defaultQuery = useQuery({
        queryKey: settingsKey(branchKey, null),
        queryFn: () => getReferralSettings(branchId, null),
        enabled: canManage && Boolean(branchId),
        retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
    });

    const staffRows = useMemo(
        () => Array.isArray(staffQuery.data?.staff) ? staffQuery.data.staff : [],
        [staffQuery.data],
    );
    const settingsList = useMemo(
        () => Array.isArray(listQuery.data) ? listQuery.data : [],
        [listQuery.data],
    );
    const selectedHasOverride = selectedInviterId !== null && settingsList.some(
        (settings) => inviterMatches(settings, selectedInviterId),
    );
    const overrideQuery = useQuery({
        queryKey: settingsKey(branchKey, selectedInviterId),
        queryFn: () => getReferralSettings(branchId, selectedInviterId),
        enabled: canManage
            && Boolean(branchId)
            && selectedInviterId !== null
            && listQuery.isSuccess
            && selectedHasOverride,
        retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
    });
    const missingOverride = selectedInviterId !== null && (
        (listQuery.isSuccess && !selectedHasOverride) || isNotFound(overrideQuery.error)
    );
    const defaultMissing = isNotFound(defaultQuery.error);
    const selectedStaff = staffRows.find((person) => Number(person.id) === selectedInviterId) || null;
    const currentSettings = selectedInviterId === null
        ? defaultQuery.data
        : missingOverride
            ? defaultQuery.data
            : overrideQuery.data;
    const currentRevision = selectedInviterId === null
        ? defaultMissing ? null : defaultQuery.data?.revision || null
        : missingOverride ? null : overrideQuery.data?.revision || null;
    const currentSettingsSignature = JSON.stringify({
        selector: selectedKey,
        settings: currentSettings ? {
            id: currentSettings.id,
            distribution_type: currentSettings.distribution_type,
            inviter_personal: currentSettings.inviter_personal,
            revision: currentSettings.revision,
            shares: (currentSettings.shares || []).map((share) => ({
                id: share.id,
                personal: share.personal,
                is_place_share: share.is_place_share,
                percent: share.percent,
            })),
        } : null,
    });

    useEffect(() => {
        if (!canManage) return;
        if (selectedInviterId !== null && selectedHasOverride && overrideQuery.isLoading) return;
        if (hydratedSettingsSignature.current === currentSettingsSignature) return;
        hydratedSettingsSignature.current = currentSettingsSignature;
        const nextDraft = referralSettingsToDraft(currentSettings || {}, staffRows);
        setDraft({
            ...nextDraft,
            inviter_personal_id: selectedInviterId,
            shares: nextDraft.shares.map((share) => ({
                ...share,
                _key: share.id ? `share-${share.id}` : `loaded-${nextRowKey.current++}`,
            })),
        });
        setValidationMessage("");
    }, [
        canManage,
        currentSettings,
        currentSettingsSignature,
        overrideQuery.isLoading,
        selectedHasOverride,
        selectedInviterId,
        staffRows,
    ]);

    const updateCaches = (settings, inviterId) => {
        const exactKey = settingsKey(branchKey, inviterId);
        queryClient.setQueryData(exactKey, settings);
        queryClient.setQueryData(listQueryKey, (current) => {
            const rows = Array.isArray(current) ? current : [];
            const withoutSelected = rows.filter((item) => !inviterMatches(item, inviterId));
            return settings ? [...withoutSelected, settings] : withoutSelected;
        });
        void queryClient.invalidateQueries({queryKey: listQueryKey});
        void queryClient.invalidateQueries({queryKey: exactKey});
    };

    const refreshAfterConflict = async (inviterId) => {
        const exactKey = settingsKey(branchKey, inviterId);
        hydratedSettingsSignature.current = null;
        await Promise.all([
            queryClient.invalidateQueries({queryKey: listQueryKey}),
            queryClient.invalidateQueries({queryKey: exactKey}),
        ]);
    };

    const saveMutation = useMutation({
        mutationFn: ({payload, inviterId}) => payload.distribution_type === "custom"
            ? updateReferralSettings(branchId, payload, inviterId)
            : quickSetupReferralSettings(branchId, payload, inviterId),
        onSuccess: (settings, variables) => {
            updateCaches(settings, variables.inviterId);
            setResult({kind: "success", message: t("orgs.referral.saved")});
        },
        onError: (error, variables) => {
            setResult(referralApiError(error));
            if (isConflict(error)) void refreshAfterConflict(variables.inviterId);
        },
    });
    const quickSetupMutation = useMutation({
        mutationFn: ({mode, inviterId}) => quickSetupReferralSettings(
            branchId,
            {distribution_type: mode, shares: [], revision: currentRevision},
            inviterId,
        ),
        onSuccess: (settings, variables) => {
            updateCaches(settings, variables.inviterId);
            setResult({kind: "success", message: t("orgs.referral.quickApplied")});
        },
        onError: (error, variables) => {
            setResult(referralApiError(error));
            if (isConflict(error)) void refreshAfterConflict(variables.inviterId);
        },
    });
    const deleteMutation = useMutation({
        mutationFn: ({inviterId, revision}) => deleteReferralSettings(
            branchId,
            inviterId,
            revision,
        ),
        onSuccess: (_response, variables) => {
            const {inviterId} = variables;
            const exactKey = settingsKey(branchKey, inviterId);
            queryClient.setQueryData(listQueryKey, (current) => (
                Array.isArray(current)
                    ? current.filter((item) => !inviterMatches(item, inviterId))
                    : []
            ));
            queryClient.removeQueries({queryKey: exactKey, exact: true});
            void queryClient.invalidateQueries({queryKey: listQueryKey});
            void queryClient.invalidateQueries({queryKey: exactKey});
            setDeleteOpen(false);
            setDeleteError(null);
            setResult({
                kind: "success",
                message: t("orgs.referral.deleted"),
            });
        },
        onError: (error, variables) => {
            setDeleteError(referralApiError(error));
            if (isConflict(error)) void refreshAfterConflict(variables.inviterId);
        },
    });
    const pending = saveMutation.isPending || quickSetupMutation.isPending || deleteMutation.isPending;

    if (!canManage) return null;

    const updateDraft = (updater) => {
        setDraft((current) => typeof updater === "function" ? updater(current) : updater);
        setValidationMessage("");
        setResult(null);
    };

    const selectTarget = (rowKey, target) => updateDraft((current) => ({
        ...current,
        shares: current.shares.map((share) => {
            if (share._key !== rowKey) return share;
            if (target === "place") {
                return {...share, personal_id: null, is_place_share: true, personal_name: branchName || t("orgs.referral.placeFallback")};
            }
            if (target.startsWith("staff:")) {
                const personalId = Number(target.slice("staff:".length));
                const person = staffRows.find((item) => Number(item.id) === personalId);
                return {
                    ...share,
                    personal_id: personalId,
                    is_place_share: false,
                    personal_name: person?.name || "",
                };
            }
            return {...share, personal_id: null, is_place_share: false, personal_name: ""};
        }),
    }));

    const addShare = () => updateDraft((current) => ({
        ...current,
        shares: [
            ...current.shares,
            {
                _key: `new-${nextRowKey.current++}`,
                personal_id: null,
                is_place_share: false,
                personal_name: "",
                percent: "",
            },
        ],
    }));

    const saveDraft = () => {
        setValidationMessage("");
        setResult(null);
        const normalizedDraft = {
            ...draft,
            shares: draft.shares.map(sharePayload),
        };
        const validation = validateReferralDraft(normalizedDraft);
        if (!validation.valid) {
            setValidationMessage(validation.message);
            return;
        }
        const activeStaffIds = new Set(staffRows.map((person) => Number(person.id)));
        if (normalizedDraft.distribution_type === "custom" && normalizedDraft.shares.some(
            (share) => !share.is_place_share && !activeStaffIds.has(share.personal_id),
        )) {
            setValidationMessage(t("orgs.referral.activeStaffRequired"));
            return;
        }
        const payload = normalizedDraft.distribution_type === "custom"
            ? {
                distribution_type: "custom",
                shares: normalizedDraft.shares,
                revision: currentRevision,
            }
            : {
                distribution_type: normalizedDraft.distribution_type,
                shares: [],
                revision: currentRevision,
            };
        saveMutation.mutate({payload, inviterId: selectedInviterId});
    };

    const loadingSettings = listQuery.isLoading || defaultQuery.isLoading;
    const loadingOverride = selectedInviterId !== null && selectedHasOverride && overrideQuery.isLoading;
    const settingsError = listQuery.isError
        ? listQuery.error
        : defaultQuery.isError && !defaultMissing
            ? defaultQuery.error
            : overrideQuery.isError && !isNotFound(overrideQuery.error)
                ? overrideQuery.error
                : null;
    const total = totalPercent(draft.shares);
    const modes = MODE_VALUES.map((value) => ({
        value,
        label: t(`orgs.referral.modes.${value}.label`),
        description: t(`orgs.referral.modes.${value}.description`),
    }));
    const presetModes = PRESET_MODE_VALUES.map((value) => modes.find((mode) => mode.value === value));

    return (
        <section className="referral-workspace" aria-labelledby="referral-settings-heading">
            <header className="referral-workspace-header">
                <div>
                    <p className="admin-eyebrow">{t("orgs.referral.eyebrow")}</p>
                    <h2 id="referral-settings-heading">{t("orgs.referral.title")}</h2>
                    <p>{t("orgs.referral.copy")}</p>
                </div>
                <label className="referral-selector">
                    <span>{t("orgs.referral.selector")}</span>
                    <select
                        value={selectedKey}
                        disabled={pending || staffQuery.isLoading}
                        onChange={(event) => {
                            setSelectedKey(event.target.value);
                            setValidationMessage("");
                            setResult(null);
                            setDeleteError(null);
                            setDeleteOpen(false);
                        }}
                    >
                        <option value={DEFAULT_SELECTOR}>{t("orgs.referral.branchSetting")}</option>
                        {staffRows.map((person) => {
                            const hasOverride = settingsList.some((settings) => inviterMatches(settings, Number(person.id)));
                            return (
                                <option key={person.id} value={person.id}>
                                    {t("orgs.referral.option", {
                                        name: person.name || t("orgs.common.staffNumber", {id: person.id}),
                                        status: hasOverride ? t("orgs.referral.personal") : t("orgs.referral.inherits"),
                                    })}
                                </option>
                            );
                        })}
                    </select>
                </label>
            </header>

            {loadingSettings ? (
                <p className="referral-state-line" role="status">{t("orgs.referral.loading")}</p>
            ) : settingsError ? (
                <p className="referral-state-line referral-state-error" role="alert">
                    {mapApiError(settingsError).message}
                </p>
            ) : (
                <>
                    <div className="referral-context-bar">
                        <div>
                            <strong>
                                {selectedInviterId === null
                                    ? defaultMissing ? t("orgs.referral.baseMissing") : t("orgs.referral.branchSetting")
                                    : missingOverride ? t("orgs.referral.inheritsBranch") : t("orgs.referral.personalRule")}
                            </strong>
                            <span>
                                {selectedInviterId === null
                                    ? defaultMissing
                                        ? t("orgs.referral.saveHint")
                                        : branchName || t("orgs.common.branchNumber", {id: branchId})
                                    : missingOverride
                                        ? t("orgs.referral.personalMissing")
                                        : selectedStaff?.name || t("orgs.common.staffNumber", {id: selectedInviterId})}
                            </span>
                        </div>
                        {selectedInviterId !== null && selectedHasOverride && !missingOverride ? (
                            <button
                                className="referral-delete-button"
                                type="button"
                                aria-label={t("orgs.referral.deleteLabel")}
                                title={t("orgs.referral.deleteLabel")}
                                disabled={pending}
                                onClick={() => {
                                    setDeleteError(null);
                                    setDeleteOpen(true);
                                }}
                            >
                                <Trash2 aria-hidden="true" size={15} /> {t("orgs.referral.deleteRule")}
                            </button>
                        ) : null}
                    </div>

                    {staffQuery.isError ? (
                        <p className="referral-state-line referral-state-error" role="alert">
                            {mapApiError(staffQuery.error).message}
                        </p>
                    ) : !staffQuery.isLoading && staffRows.length === 0 ? (
                        <p className="referral-state-line">
                            {t("orgs.referral.noStaff")}
                        </p>
                    ) : null}

                    {loadingOverride ? (
                        <p className="referral-state-line" role="status">{t("orgs.referral.loadingPersonal")}</p>
                    ) : (
                        <>
                            <fieldset className="referral-mode-fieldset" disabled={pending}>
                                <legend>{t("orgs.referral.scheme")}</legend>
                                <div className="referral-mode-grid">
                                    {modes.map((mode) => (
                                        <label
                                            className={draft.distribution_type === mode.value ? "is-selected" : ""}
                                            key={mode.value}
                                        >
                                            <input
                                                checked={draft.distribution_type === mode.value}
                                                name="referral-distribution-type"
                                                type="radio"
                                                value={mode.value}
                                                onChange={() => updateDraft((current) => ({
                                                    ...current,
                                                    distribution_type: mode.value,
                                                }))}
                                            />
                                            <strong>{mode.label}</strong>
                                            <span>{mode.description}</span>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>

                            <div className="referral-quick-strip">
                                <div>
                                    <Zap aria-hidden="true" size={15} />
                                    <span>{t("orgs.referral.quick")}</span>
                                </div>
                                <div className="referral-quick-actions">
                                    {presetModes.map((mode) => {
                                        const applying = quickSetupMutation.isPending
                                            && quickSetupMutation.variables?.mode === mode.value;
                                        return (
                                            <button
                                                className="admin-button admin-button-secondary"
                                                type="button"
                                                key={mode.value}
                                                aria-label={applying ? t("orgs.referral.applying") : t("orgs.referral.apply", {label: mode.label})}
                                                title={t("orgs.referral.applyTitle", {label: mode.label})}
                                                disabled={pending}
                                                onClick={() => {
                                                    setValidationMessage("");
                                                    setResult(null);
                                                    quickSetupMutation.mutate({
                                                        mode: mode.value,
                                                        inviterId: selectedInviterId,
                                                    });
                                                }}
                                            >
                                                {applying ? t("orgs.referral.applying") : mode.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {draft.distribution_type === "custom" ? (
                                <section className="referral-custom-editor" aria-labelledby="referral-custom-heading">
                                    <header>
                                        <div>
                                            <h3 id="referral-custom-heading">{t("orgs.referral.sharesTitle")}</h3>
                                            <p>{t("orgs.referral.sharesCopy")}</p>
                                        </div>
                                        <strong className={total === 100 ? "is-complete" : ""}>
                                            {t("orgs.referral.total", {percent: displayPercent(total)})}
                                        </strong>
                                    </header>
                                    {draft.shares.length ? (
                                        <div className="referral-share-list">
                                            {draft.shares.map((share, index) => (
                                                <div className="referral-share-row" key={share._key}>
                                                    <label>
                                                        <span>{t("orgs.referral.recipient", {index: index + 1})}</span>
                                                        <select
                                                            aria-label={t("orgs.referral.recipient", {index: index + 1})}
                                                            value={share.is_place_share
                                                                ? "place"
                                                                : share.personal_id !== null && share.personal_id !== undefined
                                                                    ? `staff:${share.personal_id}`
                                                                    : ""}
                                                            disabled={pending}
                                                            onChange={(event) => selectTarget(share._key, event.target.value)}
                                                        >
                                                            <option value="">{t("orgs.referral.chooseRecipient")}</option>
                                                            <option value="place">{t("orgs.common.branch")}</option>
                                                            {staffRows.map((person) => (
                                                                <option key={person.id} value={`staff:${person.id}`}>
                                                                    {person.name || t("orgs.common.staffNumber", {id: person.id})}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <label>
                                                        <span>{t("orgs.referral.percent", {index: index + 1})}</span>
                                                        <input
                                                            aria-label={t("orgs.referral.percent", {index: index + 1})}
                                                            type="number"
                                                            inputMode="decimal"
                                                            min="0.01"
                                                            max="100"
                                                            step="0.01"
                                                            value={share.percent}
                                                            disabled={pending}
                                                            onChange={(event) => updateDraft((current) => ({
                                                                ...current,
                                                                shares: current.shares.map((item) => item._key === share._key
                                                                    ? {...item, percent: event.target.value}
                                                                    : item),
                                                            }))}
                                                        />
                                                    </label>
                                                    <button
                                                        className="referral-row-remove"
                                                        type="button"
                                                        aria-label={t("orgs.referral.removeShare", {index: index + 1})}
                                                        title={t("orgs.referral.removeShareTitle")}
                                                        disabled={pending}
                                                        onClick={() => updateDraft((current) => ({
                                                            ...current,
                                                            shares: current.shares.filter((item) => item._key !== share._key),
                                                        }))}
                                                    >
                                                        <Trash2 aria-hidden="true" size={15} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="referral-empty-shares">{t("orgs.referral.emptyShares")}</p>
                                    )}
                                    <button
                                        className="admin-button admin-button-secondary referral-add-share"
                                        type="button"
                                        disabled={pending}
                                        onClick={addShare}
                                    >
                                        <Plus aria-hidden="true" size={15} /> {t("orgs.referral.addShare")}
                                    </button>
                                </section>
                            ) : null}

                            {validationMessage ? (
                                <p className="referral-validation-error" role="alert">{validationMessage}</p>
                            ) : null}
                            <MutationResultBanner result={result} />

                            <footer className="referral-workspace-footer">
                                <span><SlidersHorizontal aria-hidden="true" size={14} /> {t("orgs.referral.appliesToAll")}</span>
                                <button
                                    className="admin-button admin-button-primary"
                                    type="button"
                                    disabled={pending}
                                    onClick={saveDraft}
                                >
                                    <Save aria-hidden="true" size={15} />
                                    {saveMutation.isPending ? t("orgs.common.saving") : t("orgs.referral.save")}
                                </button>
                            </footer>
                        </>
                    )}
                </>
            )}

            <ConfirmationDialog
                open={deleteOpen}
                title={t("orgs.referral.deleteTitle")}
                description={t("orgs.referral.deleteCopy")}
                entity={selectedStaff?.name || t("orgs.common.staffNumber", {id: selectedInviterId})}
                confirmationText={selectedStaff?.name || t("orgs.common.staffNumber", {id: selectedInviterId})}
                confirmLabel={t("orgs.referral.deleteRule")}
                tone="destructive"
                pending={deleteMutation.isPending}
                error={deleteError}
                dialogKey={`delete-referral-settings-${selectedInviterId || ""}`}
                onCancel={() => {
                    if (deleteMutation.isPending) return;
                    setDeleteOpen(false);
                    setDeleteError(null);
                }}
                onConfirm={() => deleteMutation.mutate({
                    inviterId: selectedInviterId,
                    revision: currentRevision,
                })}
            />
        </section>
    );
};
